/**
 * Serwis API dla modulu Grafik Pracy
 * Endpointy admin (grafik.edit/view_all) + endpointy pracownika (grafik.view_own)
 */

import api from './api';
import { API_URL } from '../config/environment';

const buildQuery = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') {
      qs.set(k, String(v));
    }
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

export const grafikService = {
  // ===== ADMIN =====

  /**
   * Pobierz wszystkie zmiany w danym miesiacu (dla wszystkich pracownikow)
   */
  getMonth: async ({ year, month, locationId } = {}) => {
    try {
      const url = `grafik/month${buildQuery({ year, month, location_id: locationId })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania miesiaca:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania grafiku');
    }
  },

  /**
   * Pobierz zmiany konkretnego pracownika w danym miesiacu
   */
  getUserShifts: async (login, { year, month, locationId } = {}) => {
    try {
      const url = `grafik/user/${encodeURIComponent(login)}${buildQuery({ year, month, location_id: locationId })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania zmian pracownika:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania zmian pracownika');
    }
  },

  /**
   * Lista pracownikow z sumami godzin
   */
  getUsersList: async ({ locationId, year, month } = {}) => {
    try {
      const url = `grafik/users-list${buildQuery({ location_id: locationId, year, month })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania listy pracownikow:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania listy pracownikow');
    }
  },

  /**
   * Pobierz szczegoly pojedynczej zmiany
   */
  getShift: async (shiftId) => {
    try {
      const response = await api.get(`grafik/shift/${shiftId}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania zmiany:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania zmiany');
    }
  },

  /**
   * Dostepnosc pracownika w miesiacu (admin tooltip).
   * Returns: {success, data: {user_login, year, month, entries: [...]}}
   */
  getUserAvailability: async (userLogin, year, month) => {
    try {
      const url = `grafik/availability/${encodeURIComponent(userLogin)}${buildQuery({ year, month })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania dostepnosci pracownika:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania dostepnosci pracownika');
    }
  },

  /**
   * Sumy per user dla miesiaca
   */
  getSummary: async ({ year, month, locationId } = {}) => {
    try {
      const url = `grafik/summary${buildQuery({ year, month, location_id: locationId })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania podsumowania:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania podsumowania');
    }
  },

  /**
   * Utworz nowa zmiane
   * payload: {user_login, location_id, data, godzina_od, godzina_do, przerwa_minut?, status?, typ_zmiany?, notatki?}
   */
  createShift: async (payload) => {
    try {
      const response = await api.post('grafik/shift', payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad tworzenia zmiany:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad tworzenia zmiany');
    }
  },

  /**
   * Aktualizuj zmiane
   */
  updateShift: async (shiftId, payload) => {
    try {
      const response = await api.put(`grafik/shift/${shiftId}`, payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad aktualizacji zmiany:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad aktualizacji zmiany');
    }
  },

  /**
   * Usun zmiane
   */
  deleteShift: async (shiftId) => {
    try {
      const response = await api.delete(`grafik/shift/${shiftId}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad usuwania zmiany:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad usuwania zmiany');
    }
  },

  /**
   * Pobierz innych pracownikow zaplanowanych w tej samej lokalizacji+dacie.
   */
  getSameDayStaff: async ({ locationId, data, excludeUserLogin, excludeShiftId } = {}) => {
    try {
      const url = `grafik/same-day-staff${buildQuery({
        location_id: locationId,
        data,
        exclude_user_login: excludeUserLogin,
        exclude_shift_id: excludeShiftId
      })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad same-day-staff:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad sprawdzania innych pracownikow');
    }
  },

  /**
   * Sprawdz konflikty (overlap) dla zmiany
   */
  checkConflicts: async ({ userLogin, data, godzinaOd, godzinaDo, excludeId } = {}) => {
    try {
      const url = `grafik/conflicts${buildQuery({
        user_login: userLogin,
        data,
        godzina_od: godzinaOd,
        godzina_do: godzinaDo,
        exclude_id: excludeId
      })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad sprawdzania konfliktow:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad sprawdzania konfliktow');
    }
  },

  /**
   * Skopiuj zmiany z jednego tygodnia do drugiego
   * payload: {from_date, to_date, user_logins?, location_ids?, override_existing?}
   */
  copyWeek: async (payload) => {
    try {
      const response = await api.post('grafik/copy-week', payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad kopiowania tygodnia:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad kopiowania tygodnia');
    }
  },

  /**
   * Bulk dodawanie zmian
   * payload: {shifts:[...]}
   */
  bulkCreate: async (shifts) => {
    try {
      const response = await api.post('grafik/bulk', { shifts });
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad bulk-create:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad bulk-create zmian');
    }
  },

  /**
   * Eksport CSV - zwraca url do pobrania
   */
  getExportUrl: ({ year, month, locationId, format = 'csv' } = {}) => {
    return `grafik/export${buildQuery({ format, year, month, location_id: locationId })}`;
  },

  /**
   * Wykonaj eksport i pobierz blob
   */
  exportCsv: async ({ year, month, locationId } = {}) => {
    try {
      const url = `grafik/export${buildQuery({ format: 'csv', year, month, location_id: locationId })}`;
      const response = await api.get(url, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad eksportu CSV:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad eksportu CSV');
    }
  },

  /**
   * Eksport PDF - zwraca blob (A4 landscape, tabela pracownicy x dni miesiaca).
   */
  exportPdf: async ({ year, month, locationId } = {}) => {
    try {
      const url = `grafik/export${buildQuery({ format: 'pdf', year, month, location_id: locationId })}`;
      const response = await api.get(url, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad eksportu PDF:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad eksportu PDF');
    }
  },

  // ===== PRACOWNIK =====

  getMyMonth: async ({ year, month } = {}) => {
    try {
      const url = `grafik/me${buildQuery({ year, month })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania mojego miesiaca:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania mojego grafiku');
    }
  },

  getMeUpcoming: async (days = 7) => {
    try {
      const response = await api.get(`grafik/me/upcoming${buildQuery({ days })}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania nadchodzacych zmian:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania nadchodzacych zmian');
    }
  },

  getMySummary: async ({ year, month } = {}) => {
    try {
      const url = `grafik/me/summary${buildQuery({ year, month })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania mojego podsumowania:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania podsumowania');
    }
  },

  acceptShift: async (shiftId) => {
    try {
      const response = await api.post(`grafik/me/accept-shift/${shiftId}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad akceptacji zmiany:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad akceptacji zmiany');
    }
  },

  // Dostepnosc
  getMyAvailability: async ({ year, month } = {}) => {
    try {
      const url = `grafik/me/availability${buildQuery({ year, month })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania dostepnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania dostepnosci');
    }
  },

  setMyAvailability: async (payload) => {
    try {
      const response = await api.post('grafik/me/availability', payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad zapisu dostepnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad zapisu dostepnosci');
    }
  },

  deleteMyAvailability: async (id) => {
    try {
      const response = await api.delete(`grafik/me/availability/${id}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad usuwania dostepnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad usuwania dostepnosci');
    }
  },

  bulkMyAvailability: async (payload) => {
    try {
      const response = await api.post('grafik/me/availability/bulk', payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad bulk dostepnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad zapisu zbiorczego dostepnosci');
    }
  },

  // Wnioski urlopowe
  getMyLeaves: async () => {
    try {
      const response = await api.get('grafik/me/leaves');
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania wnioskow urlopowych:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania wnioskow urlopowych');
    }
  },

  submitMyLeave: async (payload) => {
    try {
      const response = await api.post('grafik/me/leaves', payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad skladania wniosku urlopowego:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad skladania wniosku');
    }
  },

  // Godziny pracy lokalizacji (domyslny harmonogram per dzien tygodnia)
  getLocationHours: async (locationId) => {
    try {
      const response = await api.get(`grafik/location-hours/${locationId}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania godzin pracy lokalizacji:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania godzin pracy');
    }
  },

  updateLocationHours: async (locationId, hours) => {
    try {
      const response = await api.put(`grafik/location-hours/${locationId}`, { hours });
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad aktualizacji godzin pracy lokalizacji:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad aktualizacji godzin pracy');
    }
  },

  // ===== ETAP 6+7: Plan vs Realizacja, Punktualnosc, Live Status =====

  /**
   * Plan vs Realizacja - polaczenie planowanych zmian z rzeczywistymi otwartymi zmianami POS.
   */
  getPlanVsReal: async ({ year, month, locationId } = {}) => {
    try {
      const url = `grafik/plan-vs-real${buildQuery({ year, month, location_id: locationId })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania plan-vs-real:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania plan-vs-real');
    }
  },

  /**
   * Raport punktualnosci per pracownik (sumy spoznien, %, liczba zmian, godziny).
   */
  getPunktualnoscRaport: async ({ year, month, locationId, userLogin } = {}) => {
    try {
      const url = `grafik/punktualnosc${buildQuery({
        year, month,
        location_id: locationId,
        user_login: userLogin
      })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania raportu punktualnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania raportu punktualnosci');
    }
  },

  /**
   * Eksport CSV raportu punktualnosci - zwraca blob.
   */
  exportPunktualnoscCsv: async ({ year, month, locationId, userLogin } = {}) => {
    try {
      const url = `grafik/punktualnosc/export${buildQuery({
        format: 'csv', year, month,
        location_id: locationId,
        user_login: userLogin
      })}`;
      const response = await api.get(url, { responseType: 'blob' });
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad eksportu CSV punktualnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad eksportu CSV punktualnosci');
    }
  },

  /**
   * URL do eksportu CSV punktualnosci (do window.open).
   */
  getPunktualnoscExportUrl: ({ year, month, locationId, userLogin } = {}) => {
    return `${API_URL}/grafik/punktualnosc/export${buildQuery({
      format: 'csv', year, month,
      location_id: locationId,
      user_login: userLogin
    })}`;
  },

  /**
   * Live status - kto teraz w pracy, spozniony, jeszcze nie pojawil sie itd.
   */
  getLiveStatus: async (locationId) => {
    try {
      const url = `grafik/live-status${buildQuery({ location_id: locationId })}`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania live status:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania live status');
    }
  },

  /**
   * Konfiguracja punktualnosci per lokalizacja: tolerancje + alert + live monitoring.
   */
  getLocationPunktualnoscConfig: async (locationId) => {
    try {
      const response = await api.get(`grafik/location-punktualnosc-config/${locationId}`);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania konfiguracji punktualnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania konfiguracji punktualnosci');
    }
  },

  updateLocationPunktualnoscConfig: async (locationId, payload) => {
    try {
      const response = await api.put(`grafik/location-punktualnosc-config/${locationId}`, payload);
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad aktualizacji konfiguracji punktualnosci:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad aktualizacji konfiguracji punktualnosci');
    }
  },

  /**
   * Manualne wywolanie skanowania absencji (cron).
   */
  triggerWykryjAbsencje: async () => {
    try {
      const response = await api.post('grafik/wykryj-absencje');
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad wykrywania absencji:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad wykrywania absencji');
    }
  },

  /**
   * Adresy feedow .ics do subskrypcji grafiku (Google Calendar / iPhone).
   * Zwraca { wlasny: {path, opis}, wszyscy?: {path, opis} } (wszyscy tylko admin).
   */
  getCalendarUrl: async () => {
    try {
      const response = await api.get('grafik/me/calendar-url');
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad pobierania URL kalendarza:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad pobierania URL kalendarza');
    }
  },

  /**
   * Generuje nowy token kalendarza (uniewaznia stary adres). typ: 'wlasny'|'wszyscy'.
   */
  resetCalendarUrl: async (typ = 'wlasny') => {
    try {
      const response = await api.post('grafik/me/calendar-url/reset', { typ });
      return response.data;
    } catch (error) {
      console.error('[Grafik] Blad resetu URL kalendarza:', error);
      throw new Error(error.response?.data?.error || error.message || 'Blad resetu URL kalendarza');
    }
  },
};

export default grafikService;
